import { useState, useEffect } from "react";
import { getDocs, addDoc, deleteDoc, query, orderBy, serverTimestamp } from "firebase/firestore";
import { tenantNotesCollectionRef, tenantNoteDocRef, incrementSongNoteCount } from "../tenantContext";

const MAX_NOTES = 10;
const MAX_CHARS = 300;
const WARN_CHARS = 270;

// Returns "just now", "N min ago", "N hours ago", or a locale date string.
function timeAgo(ts) {
  if (!ts) return "";
  const date = ts.toDate ? ts.toDate() : new Date(ts);
  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffHours = Math.floor(diffMin / 60);
  if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? "" : "s"} ago`;
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

// Up to 2 uppercase initials derived from a display name.
function initials(name) {
  if (!name) return "?";
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("");
}

function Skeleton() {
  return (
    <div className="flex flex-col gap-3 px-4 py-4">
      {[1, 2, 3].map((i) => (
        <div key={i} className="flex gap-3">
          <div className="h-8 w-8 shrink-0 animate-pulse rounded-full bg-elevated" />
          <div className="flex flex-1 flex-col gap-2 pt-1">
            <div className="h-3 w-32 animate-pulse rounded bg-elevated" />
            <div className="h-3 w-full animate-pulse rounded bg-elevated" />
          </div>
        </div>
      ))}
    </div>
  );
}

export default function NotesList({
  songId,
  tenant,
  currentUserId,
  currentUserName,
  userRole,
  onNoteCountChange,
}) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState("");
  const [posting, setPosting] = useState(false);
  const [postError, setPostError] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!tenant?.bandId || !songId) return;

    let cancelled = false;
    setLoading(true);

    const colRef = tenantNotesCollectionRef(tenant, songId);
    getDocs(query(colRef, orderBy("createdAt", "asc")))
      .then((snap) => {
        if (cancelled) return;
        setNotes(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      })
      .catch((err) => {
        if (!cancelled) console.error("Failed to load notes:", err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [tenant, songId]);

  const handleDelete = async (noteId) => {
    if (deleting) return;
    setDeleting(true);
    try {
      await deleteDoc(tenantNoteDocRef(tenant, songId, noteId));
      setNotes((prev) => prev.filter((n) => n.id !== noteId));
      setConfirmDeleteId(null);
      onNoteCountChange?.(-1);
      incrementSongNoteCount(tenant, songId, -1).catch((err) =>
        console.error("Failed to decrement noteCount:", err)
      );
    } catch (err) {
      console.error("Failed to delete note:", err);
    } finally {
      setDeleting(false);
    }
  };

  const handlePost = async () => {
    const text = draft.trim();
    if (!text || text.length > MAX_CHARS || posting) return;

    setPosting(true);
    setPostError(null);
    try {
      const colRef = tenantNotesCollectionRef(tenant, songId);
      const docRef = await addDoc(colRef, {
        text,
        authorId: currentUserId,
        authorName: currentUserName || "",
        createdAt: serverTimestamp(),
      });
      // Update local state immediately after the write succeeds.
      setNotes((prev) => [
        ...prev,
        {
          id: docRef.id,
          text,
          authorId: currentUserId,
          authorName: currentUserName || "",
          createdAt: { toDate: () => new Date() },
        },
      ]);
      setDraft("");
      onNoteCountChange?.(+1);
      // Best-effort — don't let a counter failure block the UX.
      incrementSongNoteCount(tenant, songId, 1).catch((err) =>
        console.error("Failed to increment noteCount:", err)
      );
    } catch (err) {
      console.error("Failed to post note:", err);
      setPostError(err?.message || "Failed to post note.");
    } finally {
      setPosting(false);
    }
  };

  if (loading) return <Skeleton />;

  const atCap = notes.length >= MAX_NOTES;
  const charCount = draft.length;
  const overLimit = charCount > MAX_CHARS;
  const canPost = draft.trim().length > 0 && !overLimit && !posting;

  return (
    <div className="flex flex-col">
      {/* ── Notes list ── */}
      {notes.length === 0 ? (
        <p className="px-4 py-6 text-center text-sm text-text-muted">
          No notes yet.
        </p>
      ) : (
        <ul className="flex flex-col divide-y divide-subtle">
          {notes.map((note) => {
            const displayName = note.authorName || note.displayName;
            const isOwn = note.authorId === currentUserId;
            const canDelete = isOwn || userRole === "admin";
            const canEdit = isOwn;

            return (
              <li key={note.id} className="flex gap-3 px-4 py-3">
                {/* Avatar */}
                <div
                  aria-hidden="true"
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-purple-500/25 text-xs font-semibold text-purple-300"
                >
                  {initials(displayName)}
                </div>

                {/* Body */}
                <div className="min-w-0 flex-1">
                  {/* Meta row */}
                  <div className="flex items-baseline gap-2">
                    <span className="text-sm font-medium text-text-primary">
                      {displayName || "Unknown"}
                    </span>
                    <span className="text-xs text-text-muted">{timeAgo(note.createdAt)}</span>
                  </div>

                  {/* Text */}
                  <p className="mt-1 text-sm leading-relaxed text-text-secondary">{note.text}</p>

                  {/* Actions */}
                  {(canEdit || canDelete) && (
                    <div className="mt-1.5 flex items-center gap-3">
                      {confirmDeleteId === note.id ? (
                        <>
                          <span className="text-xs text-text-secondary">Delete this note?</span>
                          <button
                            type="button"
                            disabled={deleting}
                            onClick={() => handleDelete(note.id)}
                            className="text-xs text-red-400 hover:text-red-300 disabled:opacity-50"
                          >
                            {deleting ? "Deleting…" : "Yes"}
                          </button>
                          <button
                            type="button"
                            disabled={deleting}
                            onClick={() => setConfirmDeleteId(null)}
                            className="text-xs text-text-muted hover:text-text-primary disabled:opacity-50"
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <>
                          {canEdit && (
                            <button
                              type="button"
                              onClick={() => console.log("edit note", note.id)}
                              className="text-xs text-text-muted hover:text-text-primary"
                            >
                              Edit
                            </button>
                          )}
                          {canDelete && (
                            <button
                              type="button"
                              onClick={() => setConfirmDeleteId(note.id)}
                              className="text-xs text-text-muted hover:text-red-400"
                            >
                              Delete
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {/* ── Compose area ── */}
      <div className="border-t border-border px-4 py-3">
        {atCap ? (
          <p className="text-center text-xs text-text-muted">
            {MAX_NOTES} of {MAX_NOTES} notes — delete a note to add a new one.
          </p>
        ) : (
          <div className="flex flex-col gap-2">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="Add a note about this song…"
              rows={3}
              className="w-full resize-none rounded-md border border-border bg-elevated px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:border-accent-blue/60 focus:outline-none"
            />
            <div className="flex items-center justify-between">
              <span
                className={[
                  "text-xs tabular-nums",
                  overLimit
                    ? "text-red-400"
                    : charCount >= WARN_CHARS
                    ? "text-amber-400"
                    : "text-text-muted",
                ].join(" ")}
              >
                {charCount} / {MAX_CHARS}
              </span>
              <button
                type="button"
                disabled={!canPost}
                onClick={handlePost}
                className={[
                  "rounded px-3 py-1.5 text-xs font-medium transition-colors",
                  canPost
                    ? "bg-accent-blue/20 text-accent-blue hover:bg-accent-blue/30"
                    : "cursor-not-allowed text-text-muted opacity-50",
                ].join(" ")}
              >
                {posting ? "Posting…" : "Post note"}
              </button>
            </div>
            {postError && (
              <p className="text-xs text-red-400">{postError}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
